import React from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useT } from '../i18n/useT';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';

const WhyWAI: React.FC = () => {
  const { t, r } = useT();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const reasons = r.whywai.reasons;

  return (
    <Box
      component="section"
      id="whywai"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: '#F8FAFC',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              mb: 2,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '2.8rem' },
              color: '#0F172A',
              letterSpacing: '-0.02em',
            }}
          >
            {t('whywai.title')}
          </Typography>
          <Typography
            variant="h6"
            align="center"
            sx={{ mb: 7, color: '#64748B', maxWidth: '750px', mx: 'auto', lineHeight: 1.6, fontWeight: 400 }}
          >
            {t('whywai.subtitle')}
          </Typography>
        </motion.div>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 3,
            mb: 7,
          }}
        >
          {reasons.map((reason, idx) => (
            <motion.div
              key={reason.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6, delay: 0.12 * idx }}
            >
              <Box
                sx={{
                  height: '100%',
                  p: 3.5,
                  borderRadius: 3,
                  background: 'white',
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                  position: 'relative',
                  transition: 'all 0.25s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: '#CBD5E1',
                    boxShadow: '0 14px 40px rgba(15, 23, 42, 0.06)',
                  },
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    lineHeight: 1,
                    mb: 2,
                    display: 'inline-block',
                    color: '#64748B',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    letterSpacing: '0.05em',
                  }}
                >
                  0{idx + 1}
                </Typography>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 700, color: '#0F172A', mb: 1.5, lineHeight: 1.3, letterSpacing: '-0.01em' }}>
                  {reason.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6 }}>
                  {reason.description}
                </Typography>
              </Box>
            </motion.div>
          ))}
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <Box
            sx={{
              maxWidth: 900,
              mx: 'auto',
              p: { xs: 4, md: 6 },
              borderRadius: 3,
              textAlign: 'center',
              backgroundColor: 'rgb(13, 13, 37)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background:
                  'radial-gradient(circle at 20% 30%, rgba(0, 245, 255, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(255, 0, 255, 0.08) 0%, transparent 50%)',
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography
                sx={{
                  fontSize: { xs: '1.4rem', md: '2rem' },
                  fontWeight: 800,
                  lineHeight: 1.3,
                  mb: 3,
                  background: 'linear-gradient(45deg, #00F5FF, #FF00FF)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('whywai.closing')}
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.85)',
                  fontSize: { xs: '1rem', md: '1.15rem' },
                  lineHeight: 1.6,
                  mb: 4,
                  maxWidth: 650,
                  mx: 'auto',
                }}
              >
                {t('whywai.cta')}
              </Typography>
              <Button
                component="a"
                href="mailto:info@waiworking.com?subject=Quiero%20ver%20WAI%20en%20mi%20empresa"
                size="large"
                startIcon={<EmailRoundedIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: '50px',
                  background: 'linear-gradient(45deg, #00F5FF, #FF00FF)',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1rem',
                  textTransform: 'none',
                  boxShadow: '0 12px 32px rgba(0, 245, 255, 0.35)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #00F5FF, #FF00FF)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 16px 40px rgba(255, 0, 255, 0.45)',
                  },
                }}
              >
                {t('whywai.ctaButton')}
              </Button>
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default WhyWAI;
